import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/buttons/Button';
import { Card } from '../../components/cards/Card';
import { Badge } from '../../components/common/Badge';
import { User, Mail, Phone, Building, FileText, Camera, Loader2, Save, CheckCircle, Calendar, LogOut, AlertCircle } from 'lucide-react';
import { supabase } from '../../lib/supabaseClient';

export const Profile: React.FC = () => {
  const { user, updateProfile, logout } = useAuth();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    institution: '',
    agencyName: '',
    licenseNumber: '',
    propertyCount: ''
  });

  const [passwordData, setPasswordData] = useState({
    newPassword: '',
    confirmPassword: ''
  });
  const [isChangingPassword, setIsChangingPassword] = useState(false);

  const [avatarUrl, setAvatarUrl] = useState<string>('');
  const [isUploading, setIsUploading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);

  useEffect(() => {
    if (user) {
      setFormData({
        name: user.name || '',
        phone: user.phoneNumber || '',
        institution: user.institution || '',
        agencyName: user.agencyName || '',
        licenseNumber: user.licenseNumber || '',
        propertyCount: user.propertyCount || ''
      });
      setAvatarUrl(user.avatar || '');
    }
  }, [user]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || e.target.files.length === 0) return;

    const file = e.target.files[0];
    const fileExt = file.name.split('.').pop();
    const fileName = `${user?.id}-${Date.now()}.${fileExt}`;
    const filePath = `avatars/${fileName}`;

    console.log('Uploading file:', filePath);
    setIsUploading(true);
    setMessage(null);

    const uploadWithTimeout = async () => {
      const uploadPromise = supabase.storage
        .from('property-images')
        .upload(filePath, file, { cacheControl: '3600', upsert: true });

      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Upload timed out')), 15000)
      );

      return Promise.race([uploadPromise, timeoutPromise]) as Promise<any>;
    };

    try {
      // 1. Upload to Supabase Storage with Timeout
      const { data: uploadData, error: uploadError } = await uploadWithTimeout();

      if (uploadError) {
        console.error('Storage Upload Error:', uploadError);
        throw uploadError;
      }

      console.log('Upload successful:', uploadData);

      // 2. Get Public URL
      const { data } = supabase.storage
        .from('property-images')
        .getPublicUrl(filePath);

      const publicUrl = data.publicUrl;
      console.log('Public URL:', publicUrl);
      setAvatarUrl(publicUrl);

      // 3. Auto-save avatar to profile
      const { error: updateError } = await updateProfile({ avatar: publicUrl });

      if (updateError) {
        console.error('Profile Update Error during avatar save:', updateError);
        // Don't throw here, the image is uploaded, just the profile link failed.
        setMessage({ type: 'error', text: 'Image uploaded but profile link failed. Please click Save Changes.' });
      } else {
        setMessage({ type: 'success', text: 'Profile photo updated!' });
        // Optional: reload to ensure all contexts update if they don't listen to changes
        // window.location.reload(); 
      }

    } catch (error: any) {
      console.error('Upload Process Failed:', error);
      setMessage({ type: 'error', text: error.message || 'Failed to upload image. Please try again.' });
    } finally {
      setIsUploading(false);
      // Reset input so same file can be selected again if needed
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setMessage({ type: 'error', text: 'Passwords do not match' });
      return;
    }
    if (passwordData.newPassword.length < 6) {
      setMessage({ type: 'error', text: 'Password should be at least 6 characters' });
      return;
    }

    setIsChangingPassword(true);
    setMessage(null);

    const updatePasswordWithTimeout = async () => {
      const updatePromise = supabase.auth.updateUser({ password: passwordData.newPassword });
      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Password update timed out. Check your connection.')), 10000)
      );
      return Promise.race([updatePromise, timeoutPromise]) as Promise<any>;
    };

    try {
      const { error } = await updatePasswordWithTimeout();

      if (error) throw error;

      setMessage({ type: 'success', text: 'Password updated successfully' });
      setPasswordData({ newPassword: '', confirmPassword: '' });
    } catch (error: any) {
      console.error("Password update error:", error);
      setMessage({ type: 'error', text: error.message || 'Failed to update password' });
    } finally {
      setIsChangingPassword(false);
    }
  };



  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);
    setMessage(null);

    try {
      const { error } = await updateProfile({
        name: formData.name,
        phoneNumber: formData.phone,
        institution: formData.institution,
        licenseNumber: formData.licenseNumber,
        propertyCount: formData.propertyCount
      });

      if (error) throw error;
      setMessage({ type: 'success', text: 'Profile details saved successfully.' });
    } catch (error: any) {
      setMessage({ type: 'error', text: error.message || 'Failed to update profile.' });
    } finally {
      setIsSaving(false);
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      // Navigation handled by auth state change in App.tsx usually, but safe to redirect
      window.location.href = '#/';
    } catch (error) {
      console.error("Logout failed", error);
    }
  };

  if (!user) return null;

  return (
    <div className="min-h-screen bg-slate-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-3xl mx-auto">
        <h1 className="text-3xl font-bold text-gray-900 mb-8">My Profile</h1>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">

          {/* Left Column: Avatar Card */}
          <div className="md:col-span-1">
            <Card className="p-6 flex flex-col items-center text-center h-full">
              <div className="relative group mb-4">
                <div className="w-32 h-32 rounded-full overflow-hidden border-4 border-white shadow-lg bg-gray-200">
                  <img
                    src={avatarUrl || `https://ui-avatars.com/api/?name=${user.name}`}
                    alt="Profile"
                    className="w-full h-full object-cover"
                  />
                </div>
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="absolute bottom-0 right-0 bg-[#2f5c96] text-white p-2 rounded-full shadow-md hover:bg-[#254a7a] transition-colors"
                  disabled={isUploading}
                  title="Change Photo"
                >
                  {isUploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4" />}
                </button>
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileChange}
                  className="hidden"
                  accept="image/*"
                />
              </div>

              <h2 className="text-xl font-bold text-gray-900">{user.name}</h2>
              <p className="text-gray-500 text-sm mb-3">{user.email}</p>
              <Badge color="blue" className="mb-4">{user.role}</Badge>

              <div className="w-full border-t border-gray-100 pt-4 text-left text-sm text-gray-600 space-y-3 flex-1">
                <div className="flex justify-between items-center">
                  <span className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-500" /> Status</span>
                  <span className="font-medium text-green-600">Active</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="flex items-center gap-2"><Calendar className="w-4 h-4 text-gray-400" /> Joined</span>
                  <span className="font-medium">{user.joinedDate ? new Date(user.joinedDate).toLocaleDateString() : 'N/A'}</span>
                </div>
              </div>

              <div className="w-full pt-6 mt-auto">
                <Button variant="outline" className="w-full border-red-200 text-red-600 hover:bg-red-50 hover:border-red-300" onClick={handleLogout}>
                  <LogOut className="w-4 h-4 mr-2" /> Log Out
                </Button>
              </div>
            </Card>
          </div>

          {/* Right Column: Edit Form */}
          <div className="md:col-span-2">
            <Card className="p-8 h-full">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-bold text-gray-900">Edit Details</h3>
              </div>

              {message && (
                <div className={`mb-6 p-4 rounded-lg flex items-center gap-3 ${message.type === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                  {message.type === 'success' ? <CheckCircle className="w-5 h-5" /> : <AlertCircle className="w-5 h-5" />}
                  <span>{message.text}</span>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
                    <div className="relative">
                      <User className="absolute left-3 top-2.5 text-gray-400 w-5 h-5" />
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2f5c96] outline-none"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number (Required for WhatsApp)</label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-2.5 text-gray-400 w-5 h-5" />
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        placeholder="+27..."
                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2f5c96] outline-none"
                      />
                    </div>
                  </div>

                  {/* Role Specific Fields */}
                  {user.role === 'STUDENT' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">University / Institution</label>
                      <div className="relative">
                        <Building className="absolute left-3 top-2.5 text-gray-400 w-5 h-5" />
                        <input
                          type="text"
                          name="institution"
                          value={formData.institution}
                          onChange={handleInputChange}
                          className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2f5c96] outline-none"
                        />
                      </div>
                    </div>
                  )}

                  {(user.role === 'AGENT' || user.role === 'LANDLORD') && (
                    <>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Agency / Company Name</label>
                        <div className="relative">
                          <Building className="absolute left-3 top-2.5 text-gray-400 w-5 h-5" />
                          <input
                            type="text"
                            name="agencyName"
                            value={formData.agencyName}
                            onChange={handleInputChange}
                            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2f5c96] outline-none"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">License Number / Reg No.</label>
                        <div className="relative">
                          <FileText className="absolute left-3 top-2.5 text-gray-400 w-5 h-5" />
                          <input
                            type="text"
                            name="licenseNumber"
                            value={formData.licenseNumber}
                            onChange={handleInputChange}
                            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2f5c96] outline-none"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Existing Listings Count (External)</label>
                        <div className="relative">
                          <Building className="absolute left-3 top-2.5 text-gray-400 w-5 h-5" />
                          <input
                            type="number"
                            name="propertyCount"
                            value={formData.propertyCount}
                            onChange={handleInputChange}
                            placeholder="0"
                            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2f5c96] outline-none"
                          />
                        </div>
                        <p className="text-xs text-gray-400 mt-1">Properties you manage outside this platform.</p>
                      </div>
                    </>
                  )}

                  {/* Read Only Email */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Email Address</label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-2.5 text-gray-400 w-5 h-5" />
                      <input
                        type="email"
                        value={user.email}
                        readOnly
                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg bg-gray-100 text-gray-500 cursor-not-allowed"
                      />
                    </div>
                  </div>
                </div>
                <div className="pt-6 border-t border-gray-100 mt-6">
                  <h4 className="text-lg font-bold text-gray-900 mb-4">Security / Change Password</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">New Password</label>
                      <input
                        type="password"
                        value={passwordData.newPassword}
                        onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                        placeholder="Min. 6 characters"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2f5c96] outline-none"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Confirm Password</label>
                      <input
                        type="password"
                        value={passwordData.confirmPassword}
                        onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                        placeholder="Re-enter password"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2f5c96] outline-none"
                      />
                    </div>
                  </div>
                  <div className="mt-4 flex justify-end">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={handlePasswordChange}
                      disabled={isChangingPassword || !passwordData.newPassword}
                      className="text-sm"
                    >
                      {isChangingPassword ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                      Update Password
                    </Button>
                  </div>
                </div>

                <div className="pt-4 flex justify-end">
                  <Button type="submit" disabled={isSaving} className="min-w-[140px]">
                    {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-2" /> Save Changes</>}
                  </Button>
                </div>
              </form>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};